// pages/tenantBilling/TenantPlan.jsx
// Tenants → Action → Plan (owner, 1 Oct 2026 — doc/5.10.2026.md):
//   - the current plan: features (with their frozen prices), period, amount,
//     start and end date; a renewal waiting to start;
//   - earlier plans, and every invoice — Mark paid (cash, UPI, bank
//     transfer, cheque; reference; date) and Download;
//   - RENEW / CHANGE: pick the features (scale up or down), the period and a
//     discount, at TODAY's master prices. "Renew" starts when the current
//     plan ends and switches the features on that day; "Stop the current
//     plan" starts the new one today and credits the unused part of a PAID
//     plan (old total × remaining days ÷ total days), after the discount.
// Every figure on screen comes from the server (POST …/quote), the same
// arithmetic that makes the plan — the page never prices anything itself.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
    ArrowLeft, BadgeIndianRupee, CalendarClock, Download, Loader2, RefreshCw, Receipt, X, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import useCanManage from '../../hooks/useCanManage';
import {
    PeriodRadio, DiscountControl, PlanLines, InvoiceStatusChip, PlanPhaseChip,
} from './BillingParts';
import { day, inr } from './billingFormat';
import {
    fetchTenantBilling, quoteTenantPlan, createTenantPlan, cancelTenantPlan, markTenantInvoicePaid,
    downloadTenantInvoice, billingErrorText, PAYMENT_METHODS,
} from '../../services/tenantBillingApi';

const card = 'rounded-2xl border border-gray-200 bg-white p-4 sm:p-5';
const btnPrimary = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-[13px] font-semibold shadow cursor-pointer disabled:opacity-50';
const btnGhost = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-300 text-gray-700 text-[12px] font-semibold hover:border-[#4ABDBD] hover:text-[#2D8A8A] cursor-pointer disabled:opacity-50';

const todayIndia = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

/* ---------------- the feature picker of a renewal ---------------- */

function FeaturePicker({ catalogue, selection, onChange }) {
    const { t } = useTranslation();
    const deps = catalogue.dependencies || {};
    const globalCore = new Set(catalogue.globalCoreIds || []);
    const serviceCore = catalogue.serviceCoreIds || {};
    const serviceOn = (svc) => !svc || svc === 'common' || Boolean(selection.services?.[svc]);
    const locked = (id, svc) => globalCore.has(id) || (svc && svc !== 'common' && (serviceCore[svc] || []).includes(id));

    const parentsOf = (id, seen = new Set()) => {
        (deps[id] || []).forEach((p) => { if (!seen.has(p)) { seen.add(p); parentsOf(p, seen); } });
        return seen;
    };
    const childrenOf = (id, seen = new Set()) => {
        Object.entries(deps).forEach(([child, parents]) => {
            if (parents.includes(id) && !seen.has(child)) { seen.add(child); childrenOf(child, seen); }
        });
        return seen;
    };

    const toggleFeature = (id, on) => {
        const next = { ...selection.features, [id]: on };
        if (on) parentsOf(id).forEach((p) => { next[p] = true; });
        else childrenOf(id).forEach((c) => { next[c] = false; });
        onChange({ ...selection, features: next });
    };
    const toggleService = (svc, on) => onChange({ ...selection, services: { ...selection.services, [svc]: on } });

    const services = Object.fromEntries((catalogue.services || []).map((s) => [s.id, s]));
    const groups = (catalogue.groups || []).filter((g) => (g.features || []).length);

    return (
        <div className="space-y-2.5" data-testid="plan-feature-picker">
            {groups.map((g) => {
                const svc = g.service;
                const isService = svc && svc !== 'common';
                const svcMeta = services[svc];
                const hold = svcMeta?.status === 'hold';
                const on = serviceOn(svc);
                return (
                    <div key={g.id || g.key || svc || 'core'} className={`rounded-xl border ${on && isService ? 'border-[#4ABDBD]/50' : 'border-gray-200'} overflow-hidden`}>
                        <div className="flex items-center gap-2 px-3 py-2 bg-[#F7FBFA]">
                            {isService ? (
                                <input type="checkbox" data-testid={`plan-service-${svc}`} checked={on && !hold} disabled={hold}
                                    onChange={(e) => toggleService(svc, e.target.checked)} className="accent-[#4ABDBD]" />
                            ) : null}
                            <span className="text-[13px] font-bold text-gray-700">{svcMeta?.label || g.label}</span>
                            {hold && <span className="text-[10px] font-semibold text-[#8a5a1a]">{t('tenantBilling.soon', 'Soon')}</span>}
                        </div>
                        <div className={`grid grid-cols-1 md:grid-cols-2 gap-x-4 px-3 py-1.5 ${on ? '' : 'opacity-45 pointer-events-none'}`}>
                            {g.features.map((f) => {
                                const isLocked = locked(f.id, svc);
                                const isHold = f.status === 'hold';
                                const checked = isHold ? false : (isLocked ? on : Boolean(selection.features?.[f.id]));
                                return (
                                    <label key={f.id} className={`flex items-center gap-2 py-1 text-[12px] ${isLocked || isHold ? 'text-gray-500' : 'text-gray-700 cursor-pointer'}`}>
                                        <input type="checkbox" data-testid={`plan-feature-${f.id}`} checked={checked}
                                            disabled={isLocked || isHold || !on}
                                            onChange={(e) => toggleFeature(f.id, e.target.checked)} className="accent-[#4ABDBD]" />
                                        <span className="truncate">{f.label}</span>
                                        {isLocked && <span className="text-[10px] text-gray-400">{t('tenantBilling.core', 'core')}</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ---------------- Mark paid, inline under an invoice ---------------- */

function MarkPaidForm({ invoice, onCancel, onDone, tenantId }) {
    const { t } = useTranslation();
    const [method, setMethod] = useState('upi');
    const [reference, setReference] = useState('');
    const [paidOn, setPaidOn] = useState(todayIndia());
    const [busy, setBusy] = useState(false);
    const labels = {
        cash: t('tenantBilling.method_cash', 'Cash'),
        upi: t('tenantBilling.method_upi', 'UPI'),
        bank_transfer: t('tenantBilling.method_bank_transfer', 'Bank transfer'),
        cheque: t('tenantBilling.method_cheque', 'Cheque'),
    };
    const submit = async () => {
        setBusy(true);
        try {
            const res = await markTenantInvoicePaid(tenantId, invoice.invoiceNo, { method, reference, paidOn });
            toast.success(t('tenantBilling.marked_paid', 'Invoice marked paid'));
            onDone(res?.data || null);
        } catch (err) {
            toast.error(billingErrorText(err, t('tenantBilling.save_error', 'Could not save.')));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="mt-2 rounded-xl border border-[#4ABDBD]/40 bg-[#F7FBFA] p-3 flex flex-wrap items-end gap-2" data-testid={`mark-paid-form-${invoice.invoiceNo}`}>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-600">
                {t('tenantBilling.method', 'Method')}
                <select data-testid="mark-paid-method" value={method} onChange={(e) => setMethod(e.target.value)}
                    className="tt-select px-2 py-1.5 border border-gray-300 rounded-lg text-[12px] cursor-pointer">
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{labels[m]}</option>)}
                </select>
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-600 min-w-[180px] flex-1">
                {method === 'cash' ? t('tenantBilling.reference_optional', 'Reference (optional)') : t('tenantBilling.reference', 'Transaction reference')}
                <input data-testid="mark-paid-reference" value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-600">
                {t('tenantBilling.paid_on', 'Paid on')}
                <input type="date" data-testid="mark-paid-date" value={paidOn} max={todayIndia()} onChange={(e) => setPaidOn(e.target.value)}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-[12px]" />
            </label>
            <button type="button" data-testid="mark-paid-submit" onClick={submit} disabled={busy} className={btnPrimary}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t('tenantBilling.mark_paid', 'Mark paid')}
            </button>
            <button type="button" onClick={onCancel} className={btnGhost}><X className="w-3.5 h-3.5" />{t('tenantBilling.cancel', 'Cancel')}</button>
        </div>
    );
}

/* ---------------- the page ---------------- */

export default function TenantPlan() {
    const { t } = useTranslation();
    const { tenantId } = useParams();
    const navigate = useNavigate();
    const canManage = useCanManage('tenants.manage');

    const [summary, setSummary] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [catalogue, setCatalogue] = useState(null);
    const [builderOpen, setBuilderOpen] = useState(false);
    const [mode, setMode] = useState('renew');
    const [period, setPeriod] = useState('monthly');
    const [discount, setDiscount] = useState({ type: null, value: '' });
    const [selection, setSelection] = useState(null);
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [payingInvoice, setPayingInvoice] = useState(null);

    const load = useCallback(() => {
        setLoadError(null);
        fetchTenantBilling(tenantId)
            .then(setSummary)
            .catch((err) => setLoadError(billingErrorText(err, t('tenantBilling.load_error', 'Could not load this organisation’s billing.'))));
    }, [tenantId, t]);
    useEffect(() => { load(); }, [load]);

    const state = summary?.state || {};
    const hasPlan = Boolean(summary?.plans?.some((p) => p.phase !== 'cancelled'));
    const canChange = state.kind === 'active' && !state.renewed;
    const canRenew = hasPlan && !summary?.noFee && !summary?.upcomingPlan;

    const openBuilder = async () => {
        setBuilderOpen(true);
        setMode('renew');
        setSelection(null);
        setQuote(null);
        setPeriod(summary?.currentPlan?.period || 'monthly');
        setDiscount({ type: null, value: '' });
        if (!catalogue) {
            try {
                const res = await createAuthedAxios().get('/tenants/feature-catalogue');
                setCatalogue(res.data || null);
            } catch (err) {
                setQuoteError(billingErrorText(err, t('tenantBilling.catalogue_error', 'Could not load the feature list.')));
            }
        }
    };

    // Every change asks the server. The first ask sends no selection, so the
    // answer seeds the picker with the tenant's CURRENT features; after that
    // the picker follows the server's repaired map (dependencies, cores).
    useEffect(() => {
        if (!builderOpen) return undefined;
        let live = true;
        const timer = setTimeout(() => {
            quoteTenantPlan(tenantId, {
                mode, period,
                discountType: discount.type,
                discountValue: Number(discount.value) || 0,
                ...(selection ? { services: selection.services, features: selection.features } : {}),
            })
                .then((q) => {
                    if (!live) return;
                    setQuote(q);
                    setQuoteError(null);
                    const repaired = { services: q.services || {}, features: q.featureMap || {} };
                    if (!selection || JSON.stringify(repaired) !== JSON.stringify(selection)) setSelection(repaired);
                })
                .catch((err) => { if (live) setQuoteError(billingErrorText(err, t('tenantBilling.quote_error', 'Could not price the plan.'))); });
        }, 300);
        return () => { live = false; clearTimeout(timer); };
    }, [builderOpen, tenantId, mode, period, discount.type, discount.value, selection, t]);

    const submitPlan = async () => {
        if (!quote) return;
        if (mode === 'change') {
            const confirmed = await Swal.fire({
                title: t('tenantBilling.change_confirm_title', 'Stop the current plan today?'),
                text: t('tenantBilling.change_confirm_text', 'Plan {{n}} stops today and the new plan starts now. Credit from the old plan: {{credit}}.', {
                    n: summary?.currentPlan?.planNo, credit: inr(quote.creditAmount),
                }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#2D8A8A',
                confirmButtonText: t('tenantBilling.change_confirm_yes', 'Stop and start new plan'),
                cancelButtonText: t('tenantBilling.cancel', 'Cancel'),
            });
            if (!confirmed?.isConfirmed) return;
        }
        setBusy(true);
        try {
            const res = await createTenantPlan(tenantId, {
                mode, period,
                discountType: discount.type,
                discountValue: Number(discount.value) || 0,
                services: selection?.services,
                features: selection?.features,
            });
            setSummary(res?.data || null);
            setBuilderOpen(false);
            toast.success(quote.startsNow
                ? t('tenantBilling.plan_created_now', 'New plan started, with its invoice')
                : t('tenantBilling.plan_created_later', 'Renewal saved — it starts on {{date}}', { date: day(quote.startDate) }));
        } catch (err) {
            toast.error(billingErrorText(err, t('tenantBilling.save_error', 'Could not save.')));
        } finally {
            setBusy(false);
        }
    };

    const cancelRenewal = async (plan) => {
        const confirmed = await Swal.fire({
            title: t('tenantBilling.cancel_plan_title', 'Cancel this renewal?'),
            text: t('tenantBilling.cancel_plan_text', 'Plan {{n}} will not start, and its invoice is cancelled.', { n: plan.planNo }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2D8A8A',
            confirmButtonText: t('tenantBilling.cancel_plan_yes', 'Cancel renewal'),
            cancelButtonText: t('tenantBilling.keep', 'Keep it'),
        });
        if (!confirmed?.isConfirmed) return;
        try {
            const res = await cancelTenantPlan(tenantId, plan.tenantPlanId);
            setSummary(res?.data || null);
            toast.success(t('tenantBilling.plan_cancelled', 'Renewal cancelled'));
        } catch (err) {
            toast.error(billingErrorText(err, t('tenantBilling.save_error', 'Could not save.')));
        }
    };

    const download = async (invoiceNo) => {
        try {
            await downloadTenantInvoice(tenantId, invoiceNo);
        } catch (err) {
            toast.error(billingErrorText(err, t('tenantBilling.download_error', 'Could not download the invoice.')));
        }
    };

    const invoiceOf = useMemo(
        () => Object.fromEntries((summary?.invoices || []).map((i) => [i.tenantPlanId, i])),
        [summary]
    );

    if (loadError) {
        return (
            <div className="p-4 sm:p-6">
                <p className="text-sm text-red-600" data-testid="tenant-plan-error">{loadError}</p>
            </div>
        );
    }
    if (!summary) {
        return (
            <div className="p-6 flex items-center gap-2 text-sm text-gray-500" data-testid="tenant-plan-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> {t('tenantBilling.loading', 'Loading…')}
            </div>
        );
    }

    const current = summary.currentPlan;
    const upcoming = summary.upcomingPlan;
    const earlier = summary.plans.filter((p) => p.tenantPlanId !== current?.tenantPlanId && p.tenantPlanId !== upcoming?.tenantPlanId);

    const planCard = (plan, { testId, showCancel }) => {
        const invoice = invoiceOf[plan.tenantPlanId];
        return (
            <div className={card} data-testid={testId}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-3">
                    <h3 className="text-[15px] font-bold text-gray-800">{t('tenantBilling.plan_n', 'Plan {{n}}', { n: plan.planNo })}</h3>
                    <PlanPhaseChip phase={plan.phase} />
                    <span className="text-[12px] text-gray-500">
                        {plan.period === 'yearly' ? t('tenantBilling.yearly', 'Yearly') : t('tenantBilling.monthly', 'Monthly')}
                        {' · '}{day(plan.startDate)} – {day(plan.coveredUntil || plan.endDate)}
                    </span>
                    {invoice && <InvoiceStatusChip status={invoice.status} />}
                    {showCancel && canManage && invoice?.status !== 'paid' && (
                        <button type="button" data-testid={`cancel-plan-${plan.tenantPlanId}`} onClick={() => cancelRenewal(plan)} className={`${btnGhost} ml-auto`}>
                            <X className="w-3.5 h-3.5" />{t('tenantBilling.cancel_renewal', 'Cancel renewal')}
                        </button>
                    )}
                </div>
                <PlanLines lines={plan.features} period={plan.period} featuresTotal={plan.featuresTotal}
                    discountAmount={plan.discountAmount} creditAmount={plan.creditAmount} total={plan.total}
                    testId={`${testId}-lines`} />
            </div>
        );
    };

    return (
        <div className="p-4 sm:p-6 space-y-4 max-w-5xl" data-testid="tenant-plan-page">
            <div className="flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => navigate('/tenants')} className={btnGhost}>
                    <ArrowLeft className="w-4 h-4" />{t('tenantBilling.back', 'Tenants')}
                </button>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <BadgeIndianRupee className="w-5 h-5 text-[#2D8A8A]" />
                    {t('tenantBilling.plan_page_title', 'Plan — {{name}}', { name: summary.tenantName })}
                </h1>
            </div>

            {summary.noFee ? (
                <div className={card} data-testid="tenant-plan-no-fee">
                    <p className="text-sm text-gray-600">{t('tenantBilling.no_fee_page', 'This organisation uses the platform free (No fee): it has no plan and no invoices. Switch No fee off on its Payment tab to bill it.')}</p>
                </div>
            ) : !hasPlan ? (
                <div className={card} data-testid="tenant-plan-none">
                    <p className="text-sm text-gray-600">{t('tenantBilling.no_plan_page', 'No plan yet. Make plan 1 on the organisation’s Payment tab.')}</p>
                    {canManage && (
                        <button type="button" onClick={() => navigate(`/tenants/${tenantId}/edit`)} className={`${btnGhost} mt-3`}>
                            {t('tenantBilling.go_payment_tab', 'Open the Payment tab')}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {state.kind === 'ended' && (
                        <div className="rounded-2xl border border-[#D7A05C]/50 bg-[#D7A05C]/10 px-4 py-3 text-[13px] text-[#8a5a1a] flex items-center gap-2" data-testid="tenant-plan-ended">
                            <AlertTriangle className="w-4 h-4 flex-none" />
                            {state.action === 'lock'
                                ? t('tenantBilling.state_ended_lock', 'The plan ended on {{date}} — the organisation is locked until it is renewed.', { date: day(state.endedOn) })
                                : t('tenantBilling.state_ended_message', 'The plan ended on {{date}} — they see a renewal banner.', { date: day(state.endedOn) })}
                        </div>
                    )}
                    {state.kind === 'active' && state.endingSoon && (
                        <div className="rounded-2xl border border-[#D7A05C]/50 bg-[#D7A05C]/10 px-4 py-3 text-[13px] text-[#8a5a1a] flex items-center gap-2" data-testid="tenant-plan-ending">
                            <CalendarClock className="w-4 h-4 flex-none" />
                            {t('tenantBilling.state_ending', 'The plan ends on {{date}} ({{days}} days) and is not renewed yet.', { date: day(state.endsOn), days: state.daysLeft })}
                        </div>
                    )}

                    {current && planCard(current, { testId: 'tenant-plan-current' })}
                    {upcoming && planCard(upcoming, { testId: 'tenant-plan-upcoming', showCancel: true })}

                    {canManage && canRenew && !builderOpen && (
                        <button type="button" data-testid="tenant-plan-open-builder" onClick={openBuilder} className={btnPrimary}>
                            <RefreshCw className="w-4 h-4" />{t('tenantBilling.renew_change', 'Renew / change plan')}
                        </button>
                    )}
                    {canManage && !canRenew && upcoming && (
                        <p className="text-[12px] text-gray-500">{t('tenantBilling.renewal_waiting', 'A renewal is already waiting to start. Cancel it to make a different one.')}</p>
                    )}

                    {builderOpen && (
                        <div className={`${card} space-y-4`} data-testid="tenant-plan-builder">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[15px] font-bold text-gray-800">{t('tenantBilling.renew_change', 'Renew / change plan')}</h3>
                                <button type="button" onClick={() => setBuilderOpen(false)} className={btnGhost}><X className="w-3.5 h-3.5" />{t('tenantBilling.cancel', 'Cancel')}</button>
                            </div>
                            <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="plan-mode">
                                <button type="button" role="radio" aria-checked={mode === 'renew'} data-testid="plan-mode-renew" onClick={() => setMode('renew')}
                                    className={`flex-1 min-w-[200px] text-left rounded-xl border px-3 py-2.5 cursor-pointer ${mode === 'renew' ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200'}`}>
                                    <span className="block text-[13px] font-semibold text-gray-700">{t('tenantBilling.mode_renew', 'Renew')}</span>
                                    <span className="block text-[11px] text-gray-500">
                                        {state.kind === 'active'
                                            ? t('tenantBilling.mode_renew_hint', 'Starts when the current plan ends ({{date}}); the features switch on that day.', { date: day(state.endsOn) })
                                            : t('tenantBilling.mode_renew_now_hint', 'The plan has ended, so the new one starts today.')}
                                    </span>
                                </button>
                                {canChange && (
                                    <button type="button" role="radio" aria-checked={mode === 'change'} data-testid="plan-mode-change" onClick={() => setMode('change')}
                                        className={`flex-1 min-w-[200px] text-left rounded-xl border px-3 py-2.5 cursor-pointer ${mode === 'change' ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200'}`}>
                                        <span className="block text-[13px] font-semibold text-gray-700">{t('tenantBilling.mode_change', 'Stop current plan, start new today')}</span>
                                        <span className="block text-[11px] text-gray-500">{t('tenantBilling.mode_change_hint', 'The unused part of the current plan becomes a credit — only if its invoice is paid.')}</span>
                                    </button>
                                )}
                            </div>

                            <div>
                                <p className="text-[12px] font-semibold text-gray-600 mb-2">{t('tenantBilling.features_title', 'Features (scale up or down)')}</p>
                                {catalogue && selection ? (
                                    <FeaturePicker catalogue={catalogue} selection={selection} onChange={setSelection} />
                                ) : (
                                    <p className="flex items-center gap-2 text-[12px] text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />{t('tenantBilling.loading', 'Loading…')}</p>
                                )}
                            </div>

                            <PeriodRadio value={period} onChange={setPeriod} />
                            {quoteError && <p className="text-[12px] text-red-600" data-testid="plan-quote-error">{quoteError}</p>}
                            {quote && (
                                <>
                                    <PlanLines lines={quote.features} period={period} featuresTotal={quote.featuresTotal}
                                        discountAmount={quote.discountAmount} creditAmount={quote.creditAmount} total={quote.total}
                                        testId="plan-quote-lines" />
                                    <p className="text-[12px] text-gray-500" data-testid="plan-quote-dates">
                                        {t('tenantBilling.quote_dates', 'Plan runs {{start}} to {{end}}. Prices are today’s and stay fixed for this plan.', { start: day(quote.startDate), end: day(quote.endDate) })}
                                    </p>
                                </>
                            )}
                            <DiscountControl discount={discount} onChange={setDiscount}
                                cap={discount.type === 'percentage' ? 100 : (quote?.featuresTotal ?? Infinity)} />
                            <button type="button" data-testid="plan-submit" onClick={submitPlan} disabled={busy || !quote} className={btnPrimary}>
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeIndianRupee className="w-4 h-4" />}
                                {mode === 'change' ? t('tenantBilling.submit_change', 'Stop and start new plan') : t('tenantBilling.submit_renew', 'Save renewal')}
                            </button>
                        </div>
                    )}

                    {earlier.length > 0 && (
                        <div className={card} data-testid="tenant-plan-earlier">
                            <h3 className="text-[15px] font-bold text-gray-800 mb-2">{t('tenantBilling.earlier_plans', 'Earlier plans')}</h3>
                            <div className="divide-y divide-gray-100">
                                {earlier.map((p) => (
                                    <div key={p.tenantPlanId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[12px] text-gray-700">
                                        <span className="font-semibold">{t('tenantBilling.plan_n', 'Plan {{n}}', { n: p.planNo })}</span>
                                        <PlanPhaseChip phase={p.phase} />
                                        <span>{p.period === 'yearly' ? t('tenantBilling.yearly', 'Yearly') : t('tenantBilling.monthly', 'Monthly')}</span>
                                        <span>{day(p.startDate)} – {day(p.coveredUntil || p.endDate)}</span>
                                        <span className="ml-auto font-semibold tabular-nums">{inr(p.total)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {(summary.invoices || []).length > 0 && (
                <div className={card} data-testid="tenant-plan-invoices">
                    <h3 className="text-[15px] font-bold text-gray-800 mb-2 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#2D8A8A]" />{t('tenantBilling.invoices', 'Invoices')}</h3>
                    <div className="divide-y divide-gray-100">
                        {summary.invoices.map((inv) => (
                            <div key={inv.invoiceNo} className="py-2.5" data-testid={`invoice-row-${inv.invoiceNo}`}>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-gray-700">
                                    <span className="font-semibold">{inv.invoiceNo}</span>
                                    <span className="text-gray-500">{day(inv.issueDate)}</span>
                                    <span className="text-gray-500">{day(inv.periodStart)} – {day(inv.periodEnd)}</span>
                                    <InvoiceStatusChip status={inv.status} testId={`invoice-status-${inv.invoiceNo}`} />
                                    {inv.status === 'paid' && inv.paidOn && (
                                        <span className="text-gray-500">{t('tenantBilling.paid_on_short', 'paid {{date}}', { date: day(inv.paidOn) })}{inv.paymentReference ? ` · ${inv.paymentReference}` : ''}</span>
                                    )}
                                    <span className="ml-auto font-semibold tabular-nums">{inr(inv.total)}</span>
                                    <button type="button" data-testid={`invoice-download-${inv.invoiceNo}`} onClick={() => download(inv.invoiceNo)} className={btnGhost}>
                                        <Download className="w-3.5 h-3.5" />{t('tenantBilling.download', 'Download')}
                                    </button>
                                    {canManage && inv.status === 'unpaid' && payingInvoice !== inv.invoiceNo && (
                                        <button type="button" data-testid={`invoice-mark-paid-${inv.invoiceNo}`} onClick={() => setPayingInvoice(inv.invoiceNo)} className={btnGhost}>
                                            <CheckCircle2 className="w-3.5 h-3.5" />{t('tenantBilling.mark_paid', 'Mark paid')}
                                        </button>
                                    )}
                                </div>
                                {payingInvoice === inv.invoiceNo && (
                                    <MarkPaidForm invoice={inv} tenantId={tenantId} onCancel={() => setPayingInvoice(null)}
                                        onDone={(data) => { setPayingInvoice(null); if (data) setSummary(data); else load(); }} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
